import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../../utils/constants/permissions";
import { btn } from "../../../../../components/Button/buttonStyles";

const VendorScopedSection = ({
  vendorId,
  toggleLabel,
  helperText,
  sectionTitle,
  containerMaxWidth = "max-w-2xl",
  editModalTitle,
  editModalMaxWidth = "max-w-2xl",
  fetchData,
  onToggle,
  renderTable,
  renderEdit,
}) => {
  const [worksWithAll, setWorksWithAll] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showEditPage, setShowEditPage] = useState(false);
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.VENDORS);

  const refresh = async () => {
    try {
      setLoading(true);
      const { worksWithAll: w, items: i } = await fetchData(vendorId);
      setWorksWithAll(!!w);
      setItems(Array.isArray(i) ? i : []);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const handleToggle = async () => {
    try {
      await onToggle(vendorId, !worksWithAll);
      await refresh();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to update vendor settings");
    }
  };

  const handleUpdateComplete = () => {
    setShowEditPage(false);
    refresh();
  };

  if (loading) {
    return <div className="text-center mt-10 text-gray-500 text-sm">Loading...</div>;
  }

  return (
    <div className={`space-y-5 ${containerMaxWidth}`}>
      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="text-danger-900 hover:text-danger-700 font-bold ml-2"
          >
            ✕
          </button>
        </div>
      )}

      <div className="bg-white shadow-sm border border-gray-100 rounded-lg px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-700 font-medium">{toggleLabel}</span>
          <button
            onClick={handleToggle}
            disabled={readOnly}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${
              worksWithAll ? "bg-brand-600" : "bg-gray-300"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
                worksWithAll ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium ${
              worksWithAll ? "text-brand-600" : "text-gray-500"
            }`}
          >
            {worksWithAll ? "Enabled" : "Disabled"}
          </span>
        </div>
        {worksWithAll && helperText && (
          <p className="text-xs text-gray-500 italic">{helperText}</p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {sectionTitle}
          </h3>
          {!readOnly && (
            <button
              onClick={() => setShowEditPage(true)}
              disabled={worksWithAll}
              title={
                worksWithAll
                  ? `Disable '${toggleLabel.replace(/:$/, "")}' to edit`
                  : ""
              }
              className={btn("primary", "sm")}
            >
              Update
            </button>
          )}
        </div>

        {renderTable({ items, worksWithAll })}
      </div>

      {showEditPage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowEditPage(false)}
        >
          <div
            className={`bg-white rounded-lg shadow-xl w-full ${editModalMaxWidth} max-h-[85vh] flex flex-col relative mx-4`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                {editModalTitle}
              </h2>
              <button
                onClick={() => setShowEditPage(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {renderEdit({ onComplete: handleUpdateComplete })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorScopedSection;
